import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";

export const CLI_AUTH_TOKEN_SCOPES = [
  "capabilities:read",
  "tasks:read",
  "tasks:run",
  "documents:read",
  "documents:write",
  "publishing:read",
  "publishing:write",
  "publishing:submit",
  "publishing:handoff",
] as const;

export type CliAuthTokenScope = typeof CLI_AUTH_TOKEN_SCOPES[number];

export type ProjectTokenGrant = {
  projectId: string;
  environment: string;
  agentId: string;
  purpose: string;
  brandIds: string[];
};

export type CliAuthTokenMetadata = {
  authority?: "account" | "project";
  projectGrant?: ProjectTokenGrant | null;
  id: string;
  name: string;
  scopes: CliAuthTokenScope[];
  expiresAt: string | null;
  revokedAt: string | null;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export function useCliAuthTokens() {
  return useQuery({
    queryKey: ["cli-auth-tokens"],
    queryFn: () =>
      api.get<{ data: { tokens: CliAuthTokenMetadata[] } }>("/api/cli-auth/tokens"),
  });
}

export function useCreateCliAuthToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      name: string;
      scopes: CliAuthTokenScope[];
      expiresAt?: string | null;
      projectGrant?: ProjectTokenGrant;
    }) =>
      api.post<{ data: { token: string; metadata: CliAuthTokenMetadata } }>(
        "/api/cli-auth/tokens",
        payload,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cli-auth-tokens"] });
    },
  });
}

export function useRevokeCliAuthToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tokenId: string) =>
      api.post<{ data: { tokenId: string; revoked: true } }>(
        `/api/cli-auth/tokens/${tokenId}/revoke`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cli-auth-tokens"] });
    },
  });
}
